import React from 'react';
import { Link } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClockRotateLeft } from '@fortawesome/free-solid-svg-icons';

import type { IWalletCashOut, IWalletCashOutBrand } from '@share/common-types';

import { useWalletCashOut } from '../../../wallet-cash-out/use-wallet-cash-out';
import { getAccountSectionPathById } from '../../utils';

import { BrandLogo, StatusBadge } from './shared';
import styles from './styles.module.scss';

const RECENT_COUNT = 3;

interface IRecentProps {
  accountUsername: string;
  history: IWalletCashOut[];
  brands: IWalletCashOutBrand[];
  formatMoney: (value: number, currency: string) => string;
  /** Left out of the list (the cash-out the result card already shows). */
  excludeCode?: string | null;
}

/**
 * The latest few cash-outs; each opens in Cash-out history's side panel, which also lists the rest.
 * Shown on the cash-out page (first step and result) and on the Overview.
 */
export const RecentCashOuts = ({ accountUsername, history, brands, formatMoney, excludeCode }: IRecentProps): React.ReactElement | null => {
  const intl = useIntl();
  const historyPath = getAccountSectionPathById(accountUsername, 'cashoutHistory');
  const recent = history.filter(cashOut => cashOut.cashOutCode !== excludeCode).slice(0, RECENT_COUNT);

  if (!recent.length) {
    return null;
  }

  const brandFor = (id?: string | null) => brands.find(brand => brand.productId === id);

  return (
    <section className={`${styles.card} ${styles.recent}`} aria-labelledby="account-hub-cashout-recent">
      <div className={styles.cardHeader}>
        <h2 id="account-hub-cashout-recent" className={styles.cardTitle}>
          {intl.formatMessage({ id: 'my.account.hub.cashout.recent.title' })}
        </h2>
        <Link to={historyPath} className={styles.historyLink}>
          <FontAwesomeIcon icon={faClockRotateLeft} aria-hidden="true" />
          {intl.formatMessage({ id: 'my.account.hub.cashout.recent.all' }, { count: history.length })}
        </Link>
      </div>
      <ul className={styles.recentList}>
        {recent.map(cashOut => (
          <li key={cashOut.cashOutCode}>
            <Link
              className={styles.recentRow}
              to={{ pathname: historyPath, search: new URLSearchParams({ open: cashOut.cashOutCode }).toString() }}
            >
              <BrandLogo brand={brandFor(cashOut.productId)} />
              <span className={styles.historyMain}>
                <span className={styles.historyName}>{brandFor(cashOut.productId)?.name ?? cashOut.productId ?? ''}</span>
                <span className={styles.historyMeta}>
                  {intl.formatDate(cashOut.createdAt, { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              </span>
              <span className={styles.historySide}>
                <b>{formatMoney(cashOut.payoutAmount, cashOut.currencyCode)}</b>
                <StatusBadge status={cashOut.status} />
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
};

/** Overview: loads the cash-outs itself (same calls as the cash-out page). Nothing until there are some. */
export const AccountRecentCashOuts = ({ accountUsername }: { accountUsername: string }): React.ReactElement | null => {
  const { isLoading, history, options, formatMoney } = useWalletCashOut();

  if (isLoading || !history.length) {
    return null;
  }

  return (
    <RecentCashOuts accountUsername={accountUsername} history={history} brands={options?.brands ?? []} formatMoney={formatMoney} />
  );
};
